import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faLocationDot, faPaperPlane, faUser, faMessage } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub, faFacebook, faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useTheme } from '../context/ThemeContext';
import Api from '../utils/api.js';
import toast from 'react-hot-toast';

const ContactPage = () => {
  const { darkMode } = useTheme();
    const [loading, setLoading] = useState(false); // State to manage loading

    useEffect(() => {
        // Set page title
        document.title = "Contact | Bhishan Sah - Software Engineer";
        
        // Set meta tags
        document.querySelector('meta[name="description"]').setAttribute(
            "content", 
            "Get in touch with Bhishan Sah, a professional web developer from Nepal. Available for freelance projects, collaborations, and opportunities. Contact via phone, email, or social media."
        );
        
        document.querySelector('meta[name="keywords"]').setAttribute(
            "content", 
            "contact Bhishan Sah, hire web developer, Nepal web developer, freelance developer, React developer contact, full stack developer contact"
        );

        // Update Open Graph tags for better social sharing
        document.querySelector('meta[property="og:title"]').setAttribute(
            "content", 
            "Contact Bhishan Sah | Web Developer & Software Engineer"
        );
        
        document.querySelector('meta[property="og:description"]').setAttribute(
            "content", 
            "Connect with Bhishan Sah for web development projects, collaborations, and professional opportunities. Based in Nepal, available worldwide."
        );

        // Update Twitter Card tags
        document.querySelector('meta[name="twitter:title"]').setAttribute(
            "content", 
            "Contact Bhishan Sah | Web Developer & Software Engineer"
        );
        
        document.querySelector('meta[name="twitter:description"]').setAttribute(
            "content", 
            "Connect with Bhishan Sah for web development projects, collaborations, and professional opportunities. Based in Nepal, available worldwide."
        );
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const data = {
            name: formData.get('name'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            message: formData.get('message'),
        };

        setLoading(true); // Set loading to true

        try {
            const response = await Api.post('/auth/contacts', data);

            if (response.status === 201) {
                toast.success('Message sent successfully!');
                event.target.reset(); // Clear the form
            } else {
                alert('Failed to send message.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred. Please try again.');
        } finally {
            setLoading(false); // Set loading to false after submission
        }
    };

    return (
        <div className={`relative min-h-screen py-8 -mt-10 overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'}`}>
            {/* Background decorative blobs */}
            <div className="absolute inset-0 -z-10 pointer-events-none">
                <div className={`absolute top-14 left-8 w-72 h-72 ${darkMode ? 'bg-blue-500/10' : 'bg-blue-300/20'} rounded-full blur-3xl animate-pulse`}></div>
                <div className={`absolute bottom-14 right-8 w-96 h-96 ${darkMode ? 'bg-purple-500/10' : 'bg-purple-300/20'} rounded-full blur-3xl animate-pulse delay-1000`}></div>
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${darkMode ? 'bg-gradient-to-r from-blue-500/5 to-purple-500/5' : 'bg-gradient-to-r from-blue-200/20 to-purple-200/20'} rounded-full blur-3xl`}></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${darkMode ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-gradient-to-r from-blue-500 to-purple-500'} text-white`}>
                            ✉️
                        </div>
                        <h1 className={`text-4xl font-bold mb-0 ${
                            darkMode 
                                ? "bg-gradient-to-r from-white via-blue-200 to-purple-200 text-transparent bg-clip-text"
                                : "bg-gradient-to-r from-gray-900 via-blue-700 to-purple-700 text-transparent bg-clip-text"
                        }`}>
                            Get In Touch
                        </h1>
                    </div>
                    <div className={`lg:w-28 w-24 h-1 mx-auto rounded-full mb-6 -mt-4 ${
                        darkMode ? "bg-gradient-to-r from-blue-400 to-purple-400" : "bg-gradient-to-r from-blue-600 to-purple-600"
                    }`}></div>
                    <p className={`text-lg max-w-2xl mx-auto ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                        Ready to start your next project? Let&apos;s collaborate and bring your ideas to life. 
                        I&apos;m always excited to work on new challenges and innovative solutions.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
                    {/* Contact Information Cards */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-1 space-y-6"
                    >
                        {/* Contact Info */}
                        <div className={`p-6 rounded-2xl ${
                            darkMode 
                                ? "bg-gray-800/50 border border-gray-700" 
                                : "bg-white border border-gray-200 shadow-lg"
                        }`}>
                            <h3 className={`text-xl font-bold mb-6 ${
                                darkMode ? "text-gray-100" : "text-gray-800"
                            }`}>
                                Contact Information
                            </h3>
                            <div className="space-y-4">
                                <motion.div 
                                    whileHover={{ x: 5 }}
                                    className={`flex items-center p-3 rounded-lg ${
                                        darkMode ? "bg-gray-700/50" : "bg-gray-50"
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                                        darkMode ? "bg-blue-500/20" : "bg-blue-100"
                                    }`}>
                                        <FontAwesomeIcon 
                                            icon={faPhone} 
                                            className={`${darkMode ? "text-blue-400" : "text-blue-600"}`} 
                                        />
                                    </div>
                                    <div>
                                        <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Phone</p>
                                        <a 
                                            href="tel:+447344064548" 
                                            className={`font-medium hover:underline ${
                                                darkMode ? "text-gray-200" : "text-gray-800"
                                            }`}
                                        >
                                            +447344064548
                                        </a>
                                    </div>
                                </motion.div>

                                <motion.div 
                                    whileHover={{ x: 5 }}
                                    className={`flex items-center p-3 rounded-lg ${
                                        darkMode ? "bg-gray-700/50" : "bg-gray-50"
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                                        darkMode ? "bg-green-500/20" : "bg-green-100"
                                    }`}>
                                        <FontAwesomeIcon 
                                            icon={faEnvelope} 
                                            className={`${darkMode ? "text-green-400" : "text-green-600"}`} 
                                        />
                                    </div>
                                    <div>
                                        <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Email</p>
                                        <a 
                                            href="mailto:bhishansah@gmail.com" 
                                            className={`font-medium hover:underline ${
                                                darkMode ? "text-gray-200" : "text-gray-800"
                                            }`}
                                        >
                                            bhishansah@gmail.com
                                        </a>
                                    </div>
                                </motion.div>

                                <motion.div 
                                    whileHover={{ x: 5 }}
                                    className={`flex items-center p-3 rounded-lg ${
                                        darkMode ? "bg-gray-700/50" : "bg-gray-50"
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                                        darkMode ? "bg-purple-500/20" : "bg-purple-100"
                                    }`}>
                                        <FontAwesomeIcon 
                                            icon={faLocationDot} 
                                            className={`${darkMode ? "text-purple-400" : "text-purple-600"}`} 
                                        />
                                    </div>
                                    <div>
                                        <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Location</p>
                                        <p className={`font-medium ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                                            Saptari, Nepal
                                        </p>
                                    </div>
                                </motion.div>
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className={`p-6 rounded-2xl ${
                            darkMode 
                                ? "bg-gray-800/50 border border-gray-700" 
                                : "bg-white border border-gray-200 shadow-lg"
                        }`}>
                            <h3 className={`text-xl font-bold mb-6 ${
                                darkMode ? "text-gray-100" : "text-gray-800"
                            }`}>
                                Follow Me
                            </h3>
                            <div className="grid grid-cols-3 gap-4">
                                {[
                                    { icon: faWhatsapp, href: "https://wa.me/9779819931223?text=Hello!%20How%20are%20you.", color: "text-green-500", bg: "bg-green-500/20" },
                                    { icon: faInstagram, href: "https://www.instagram.com/bhishansah/", color: "text-pink-500", bg: "bg-pink-500/20" },
                                    { icon: faLinkedin, href: "https://www.linkedin.com/in/bhishansah/", color: "text-blue-600", bg: "bg-blue-500/20" },
                                    { icon: faGithub, href: "https://github.com/visionmax01", color: darkMode ? "text-gray-300" : "text-gray-700", bg: "bg-gray-500/20" },
                                    { icon: faFacebook, href: "https://www.facebook.com/share/qJntLSmzjo3uB8HV/?mibextid=qi2Omg", color: "text-blue-800", bg: "bg-blue-500/20" },
                                ].map((social, index) => (
                                    <motion.a
                                        key={index}
                                        href={social.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        whileHover={{ scale: 1.1, y: -2 }}
                                        whileTap={{ scale: 0.95 }}
                                        className={`w-12 h-12 rounded-xl ${social.bg} flex items-center justify-center transition-all duration-300`}
                                    >
                                        <FontAwesomeIcon icon={social.icon} className={`text-lg ${social.color}`} />
                                    </motion.a>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact Form */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="lg:col-span-2"
                    >
                        <div className={`p-8 rounded-2xl ${
                            darkMode 
                                ? "bg-gray-800/50 border border-gray-700" 
                                : "bg-white border border-gray-200 shadow-lg"
                        }`}>
                            <div className="mb-8">
                                <h3 className={`text-2xl font-bold mb-2 ${
                                    darkMode ? "text-gray-100" : "text-gray-800"
                                }`}>
                                    Send Me a Message
                                </h3>
                                <p className={`${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                    Have a project in mind? Let&apos;s discuss how we can work together.
                                </p>
                            </div>

                            <form className="space-y-6" onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            darkMode ? "text-gray-300" : "text-gray-700"
                                        }`}>
                                            <FontAwesomeIcon icon={faUser} className="mr-2" />
                                            Full Name
                                        </label>
                                        <input 
                                            type="text" 
                                            name="name" 
                                            required 
                                            className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 ${
                                                darkMode 
                                                    ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-600" 
                                                    : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:bg-white"
                                            } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                            placeholder="Enter your full name"
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            darkMode ? "text-gray-300" : "text-gray-700"
                                        }`}>
                                            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                            Email Address
                                        </label>
                                        <input 
                                            type="email" 
                                            name="email" 
                                            required 
                                            className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 ${
                                                darkMode 
                                                    ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-600" 
                                                    : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:bg-white"
                                            } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                        darkMode ? "text-gray-300" : "text-gray-700"
                                    }`}>
                                        <FontAwesomeIcon icon={faPhone} className="mr-2" />
                                        Phone Number
                                    </label>
                                    <input 
                                        type="tel" 
                                        name="phone" 
                                        required 
                                        className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 ${
                                            darkMode 
                                                ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-600" 
                                                : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:bg-white"
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                        placeholder="Enter your phone number"
                                    />
                                </div>
                                
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                        darkMode ? "text-gray-300" : "text-gray-700"
                                    }`}>
                                        <FontAwesomeIcon icon={faMessage} className="mr-2" />
                                        Message
                                    </label>
                                    <textarea 
                                        name="message" 
                                        required 
                                        rows="5"
                                        className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 resize-none ${
                                            darkMode 
                                                ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500 focus:bg-gray-600" 
                                                : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:bg-white"
                                        } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                        placeholder="Tell me about your project..."
                                    />
                                </div>
                                
                                <motion.button 
                                    type="submit"
                                    disabled={loading}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`w-full py-4 px-6 rounded-lg font-semibold transition-all duration-300 ${
                                        loading 
                                            ? "bg-gray-400 cursor-not-allowed" 
                                            : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl"
                                    } text-white flex items-center justify-center`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                                            Send Message
                                        </>
                                    )}
                                </motion.button>
                            </form>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom CTA Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className={`text-center p-8 rounded-2xl ${
                        darkMode 
                            ? "bg-gradient-to-r from-gray-800/50 to-gray-700/50 border border-gray-700" 
                            : "bg-gradient-to-r from-blue-50 to-purple-50 border border-gray-200"
                    }`}
                >
                    <h3 className={`text-2xl font-bold mb-4 ${
                        darkMode ? "text-gray-100" : "text-gray-800"
                    }`}>
                        Ready to Start Your Project?
                    </h3>
                    <p className={`text-lg mb-6 ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                    }`}>
                        Let&apos;s turn your ideas into reality. I&apos;m here to help you build something amazing.
                    </p>
                    <motion.a
                        href="mailto:bhishansah@gmail.com"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        Email Me Directly
                    </motion.a>
                </motion.div>
            </div>
        </div>
    );
};

export default ContactPage;
